<template>
  <div class="payment-timing">

    <!-- This may be an order that has already been slotted into a claim period -->
    <template v-if="closedPeriodSummary">
      <table class="table is-bordered is-fullwidth has-text-centered">
        <thead>
          <tr class="has-background-light">
            <th>STATUS</th>
            <th>CUTOFF</th>
            <th>ACTUAL</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="stage in closedPeriodSummary.stages" :key="`closed_stage_${stage.status}`">
            <td>{{ stage.status }}</td>
            <td>{{ stage.date_required | toLocalDate }}</td>
            <td>
              <span v-if="stage.date_actual">
                {{ stage.date_actual | toLocalDate }}
              </span>
            </td>
          </tr>
          <tr class="has-background-success has-text-white has-text-weight-bold">
            <td colspan="2">
              CLAIMED
            </td>
            <td>
              {{ closedPeriodSummary.moment.format('MMM-YYYY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-else>
      <div v-for="(summary, i) in periodSummaries" :key="`payment_timing_period_${summary.period}`">
        <table v-if=" ! summary.is_hidden"
          class="table is-bordered is-fullwidth has-text-centered"
          :style="{ marginBottom: '2rem', opacity: summary.is_last_period ? '0.7' : 'initial' }">
          <thead>
            <tr class="has-background-dark">
              <th scope="row" colspan="3" class="has-text-white">
                {{ summary.title }}
              </th>
              <th scope="row" class="has-text-white">
                {{ summary.moment.format('MMM-YYYY') }}
              </th>
            </tr>
            <tr class="has-background-light">
              <th>STATUS</th>
              <th>CUTOFF</th>
              <th>ACTUAL</th>
              <th>RESULT</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="stage in summary.stages" :key="`payment_timing_stage_${stage.status}`">
              <td>{{ stage.status }}</td>
              <td>{{ stage.date_required | toLocalDate }}</td>
              <td>
                <span v-if="stage.date_actual">
                  {{ stage.date_actual | toLocalDate }}
                </span>
              </td>
              <td
                class="has-text-weight-bold"
                :class="{
                  'has-text-light has-background-danger': stage.is_pass == false,
                  'has-text-light has-background-success': stage.is_pass && summary.is_pass !== false
                }">
                  <span v-if="stage.is_pass === true">PASS</span>
                  <span v-if="stage.is_pass === false">FAIL</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div style="padding: 0 1rem">
      <b-checkbox :value="lateClaimRequest" :disabled="!canRequestLateClaim || !order.editable" @input="onRequestLateClaimApproval">Request Late Claim Approval</b-checkbox>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { OrderService } from "@/services/order.service.js";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";

export default {
  props: ["order"],
  data() {
    return {
      orderService: OrderService,
      period_summaries: []
    };
  },
  computed: {
    closedPeriodSummary() {
      let period = null;
      let summaries = this.periodSummaries;
      if (summaries.length) {
        period = summaries.find(p => {
          return p.is_pass; // overall pass
        });
      }
      return period;
    },
    periodSummaries() {
      let decorated_summaries = [];
      let period_summaries = this.period_summaries.slice(-2);
      period_summaries.forEach((summary, i) => {
        let decorated = {
          title: (i == period_summaries.length - 1)
            ? 'Next Claim Period'
            : 'Last Claim Period',
          is_last_period: (i == period_summaries.length - 1)
            ? false
            : true,
          is_hidden: true, // until proven otherwise
          period: summary.period,
          is_pass: summary.is_pass,
          eligible: true, // until proven otherwise
          moment: moment(summary.period),
          stages: [],
        };
        let stages = [];
        summary.stages.forEach(stage => {
          // Hide the last claim period unless it has 1+ action dates.
          // More details here: https://trello.com/c/gJ57oGUw/1593
          if (stage.date_actual) decorated.is_hidden = false;
          if ( ! decorated.is_last_period) decorated.is_hidden = false;
          if (stage.is_pass === false) { // explicit fail
            decorated.eligible = false;
          }
          let decoratedStage = Object.assign({}, stage, {
            momentActual: stage.date_actual ? moment(stage.date_actual) : null,
            momentRequired: stage.date_required ? moment(stage.date_required) : null,
            momentPossible: stage.date_possible ? moment(stage.date_possible) : null,
          });
          stages.push(decoratedStage);
        });
        decorated.stages = stages;
        decorated_summaries.push(decorated);
      });
      // Have "Next Claim Period" listed first.
      // (we need to reverse the array here)
      // https://trello.com/c/gJ57oGUw/1593
      return decorated_summaries.reverse();
    },
    canRequestLateClaim() {
      const user = this.$store.state.Auth.user;

      return (
        this.$store.getters["Auth/hasPermission"]("can manage invoices") &&
        user.firm.id === this.order.firm.id
      );
    },
    lateClaimRequest() {
      return (
        this.order.attributes &&
        !!this.order.attributes.find(
          attribute =>
            attribute.code == "[SYSTEM][ORDER][LATE_CLAIM_REQUEST]"
        )
      );
    }
  },
  mounted() {
    this.getPaymentTiming();
  },
  methods: {
    getPaymentTiming() {
      this.orderService.paymentTiming(this.order.id)
        .then(response => {
          this.period_summaries = response.data;
        })
        .catch(err => {});
    },
    async requestLateClaim() {
      try {
        const order = await this.orderService
          .requestLateClaim(this.order.id)
          .then(response => JsonDenormalizer.denormalize(response.data));

        this.order.attributes = order.attributes;

        this.$buefy.toast.open({
          message: "Late Claim has been requested.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}
    },
    async unrequestLateClaim() {
      try {
        const order = await this.orderService
          .unrequestLateClaim(this.order.id)
          .then(response => JsonDenormalizer.denormalize(response.data));

        this.order.attributes = order.attributes;

        this.$buefy.toast.open({
          message: "Late Claim has been unrequested.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}
    },
    onRequestLateClaimApproval(value) {
      if (value) {
        this.requestLateClaim();
      } else {
        this.unrequestLateClaim();
      }
    }
  }
};
</script>

<style scoped>
  table.has-text-centered td { text-align: center; }
  table.has-text-centered th { text-align: center; }
</style>


// WEBPACK FOOTER //
// src/components/Order/OrderPaymentTiming.vue